import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  endorsementCatalog,
  endorsementWizard,
  policies,
  type EndorsementDetail,
  type EndorsementDraftInput,
  type EndorsementTypeCode,
  type PolicyDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { DetailError, DetailLoading } from '@/shared/ui/DetailLayout';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Stepper } from '@/shared/ui/Stepper';
import { Textarea } from '@/shared/ui/Textarea';
import { useStepper } from '@/shared/ui/useStepper';
import { useToast } from '@/shared/ui/useToast';

import { numeroDaApolice } from '../portfolio/apolice';

import { ChangeSummaryStep } from './ChangeSummaryStep';
import {
  caminhoDoAssistente,
  codigoDeEndosso,
  passosDoEndosso,
  rotulosDosPassos,
  tituloDoAssistente,
  type PassoDoEndosso,
} from './endosso';
import { EndorsementInfo } from './EndorsementPolicyDetails';
import { EndorsementPaymentStep } from './EndorsementPaymentStep';
import { FinancialEndorsementStep } from './FinancialEndorsementStep';
import { NeutralEndorsementStep } from './NeutralEndorsementStep';
import { ReasonAndDocumentsStep } from './ReasonAndDocumentsStep';

/**
 * Assistente de endosso (`/propostas/endosso/{alteracao,cancelamento,neutro}/
 * :policyUniqueId/:subTipoEndossoId` do OnPoint; aqui
 * `/apolices/:policyId/endosso/:tipo/:subtypeId`), com o indicador de passos
 * Informacoes do endosso, Motivo e documentos, Resumo da alteracao e Informacoes de
 * pagamento (END-002 a END-013).
 *
 * O rascunho vive no servidor (`?endosso=`): voltar a tela continua de onde parou. O
 * endosso neutro vai de ponta a ponta. Alteracao e cancelamento (P-END-2) guardam os
 * campos, geram a cotacao de endosso e seguem a esteira; a emissao espera a regra do
 * premio e da restituicao.
 */
export function EndorsementWizard() {
  const { policyId = '', tipo = '', subtypeId = '' } = useParams();
  const [parametros] = useSearchParams();
  const navigate = useNavigate();
  const endossoId = parametros.get('endosso') ?? '';
  const codigo = codigoDeEndosso(tipo);

  const apolice = useQuery({
    queryKey: ['policy', policyId],
    queryFn: ({ signal }) => policies.detail(policyId, signal),
    enabled: policyId !== '',
  });
  const endosso = useQuery({
    queryKey: ['endorsement', endossoId],
    queryFn: ({ signal }) => endorsementWizard.get(endossoId, signal),
    enabled: endossoId !== '',
  });

  if (!codigo) {
    return (
      <DetailError
        title="Endosso"
        alertTitle="Tipo de endosso invalido ou nao implementado."
        message="O endereco nao corresponde a um assistente de endosso."
        backLabel="Lista de apolices"
        onBack={() => void navigate('/apolices')}
      />
    );
  }
  if (apolice.isPending || (endossoId !== '' && endosso.isPending))
    return <DetailLoading />;
  if (apolice.isError) {
    return (
      <DetailError
        title={tituloDoAssistente[codigo].titulo}
        alertTitle="Erro ao buscar apolice"
        message={apolice.error.message}
        backLabel="Lista de apolices"
        onBack={() => void navigate('/apolices')}
      />
    );
  }
  if (endosso.isError) {
    return (
      <DetailError
        title={tituloDoAssistente[codigo].titulo}
        alertTitle={`Erro ao buscar dados do ${tituloDoAssistente[codigo].titulo.toLowerCase()}`}
        message={endosso.error.message}
        backLabel="Apolice"
        onBack={() => void navigate(`/apolices/${policyId}`)}
      />
    );
  }

  if (!endosso.data) {
    return (
      <InicioDoEndosso policy={apolice.data} codigo={codigo} subtypeId={subtypeId} />
    );
  }

  return (
    <AssistenteAberto
      key={endosso.data.id}
      policy={apolice.data}
      codigo={codigo}
      endorsement={endosso.data}
    />
  );
}

/** Antes do rascunho: a escolha confirmada e o botao que abre o rascunho no servidor. */
function InicioDoEndosso({
  policy,
  codigo,
  subtypeId,
}: {
  policy: PolicyDetail;
  codigo: EndorsementTypeCode;
  subtypeId: string;
}) {
  const navigate = useNavigate();
  const elegivel = useQuery({
    queryKey: ['endorsement-eligibility', policy.id, codigo],
    queryFn: ({ signal }) => endorsementCatalog.eligibility(policy.id, codigo, signal),
    retry: false,
  });
  const iniciar = useMutation({
    mutationFn: () =>
      endorsementWizard.start(policy.id, { endorsement_subtype_id: subtypeId }),
    onSuccess: (criado) =>
      void navigate(caminhoDoAssistente(policy.id, codigo, subtypeId, criado.id), {
        replace: true,
      }),
  });
  const rascunho = elegivel.data?.drafts.find(
    (item) => item.endorsement_subtype_id === subtypeId,
  );

  return (
    <div className="flex flex-col gap-5">
      <Trilha policy={policy} codigo={codigo} />
      <PageHeader
        title={tituloDoAssistente[codigo].titulo}
        description={`Apolice ${numeroDaApolice(policy)}`}
      />
      <Card>
        <CardBody className="flex flex-col gap-3">
          {elegivel.isError ? (
            <Alert
              tone="danger"
              title={`Erro ao validar ${tituloDoAssistente[codigo].titulo.toLowerCase()}`}
            >
              {elegivel.error.message}
            </Alert>
          ) : null}
          {elegivel.data && !elegivel.data.eligible ? (
            <Alert tone="warning" title="A apolice nao aceita este endosso agora">
              <ul className="list-disc pl-4">
                {elegivel.data.reasons.map((motivo) => (
                  <li key={motivo}>{motivo}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {iniciar.isError ? (
            <Alert tone="danger" title="Nao foi possivel abrir o endosso">
              {iniciar.error.message}
            </Alert>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {rascunho ? (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    caminhoDoAssistente(policy.id, codigo, subtypeId, rascunho.id),
                    {
                      replace: true,
                    },
                  )
                }
              >
                Continuar o rascunho do endosso {rascunho.number}
              </Button>
            ) : null}
            <Button
              loading={iniciar.isPending}
              disabled={!elegivel.data?.eligible}
              onClick={() => iniciar.mutate()}
            >
              Iniciar endosso
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function Trilha({
  policy,
  codigo,
}: {
  policy: PolicyDetail;
  codigo: EndorsementTypeCode;
}) {
  return (
    <nav aria-label="Trilha" className="text-xs text-ink-500">
      <Link
        to="/apolices"
        className="text-brand-600 underline-offset-2 hover:underline"
      >
        Propostas
      </Link>{' '}
      /{' '}
      <Link
        to={`/apolices/${policy.id}`}
        className="text-brand-600 underline-offset-2 hover:underline"
      >
        Apolice {numeroDaApolice(policy)}
      </Link>{' '}
      / {tituloDoAssistente[codigo].trilha}
    </nav>
  );
}

function passoInicial(endosso: EndorsementDetail): PassoDoEndosso {
  if (endosso.status === 'rascunho') return 'informacoes-do-endosso';
  return 'informacoes-de-pagamento';
}

function AssistenteAberto({
  policy,
  codigo,
  endorsement,
}: {
  policy: PolicyDetail;
  codigo: EndorsementTypeCode;
  endorsement: EndorsementDetail;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const passo = useStepper(passosDoEndosso, passoInicial(endorsement));
  const [desistindo, setDesistindo] = useState(false);
  const [nota, setNota] = useState('');

  const podePedir = can('endossos.endosso.create');
  const rascunho = endorsement.status === 'rascunho';
  const editavel = rascunho && podePedir;
  const emAndamento = ['rascunho', 'solicitado', 'em_analise'].includes(
    endorsement.status,
  );

  function atualiza(detalhe: EndorsementDetail) {
    queryClient.setQueryData(['endorsement', endorsement.id], detalhe);
  }

  const salvar = useMutation({
    mutationFn: ({ input }: { input: EndorsementDraftInput; avancar: boolean }) =>
      endorsementWizard.update(endorsement.id, input),
    onSuccess: (detalhe, { avancar }) => {
      atualiza(detalhe);
      if (avancar) passo.next();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const enviar = useMutation({
    mutationFn: () => endorsementWizard.submit(endorsement.id),
    onSuccess: (detalhe) => {
      atualiza(detalhe);
      toast.success('Cotacao salva com sucesso.');
      void queryClient.invalidateQueries({ queryKey: ['endorsements'] });
    },
    onError: (error: Error) => toast.error(error.message || 'Erro ao salvar cotacao.'),
  });

  const desistir = useMutation({
    mutationFn: () => endorsementWizard.withdraw(endorsement.id, nota),
    onSuccess: (detalhe) => {
      atualiza(detalhe);
      setDesistindo(false);
      setNota('');
      toast.success('Endosso cancelado.');
      void queryClient.invalidateQueries({ queryKey: ['endorsements'] });
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Erro ao cancelar a cotacao'),
  });

  function proximo(input: EndorsementDraftInput | null) {
    if (input === null || !editavel) {
      passo.next();
      return;
    }
    salvar.mutate({ input, avancar: true });
  }

  const problemasPorPasso = new Set(
    (endorsement.problems ?? []).map((item) => item.step),
  );
  // O passo que o rascunho ja preencheu fica concluido e pode ser aberto de volta: quem
  // retoma o rascunho nao precisa refazer o caminho. Enviado, os tres primeiros estao
  // concluidos.
  function concluido(chave: PassoDoEndosso): boolean {
    if (!rascunho) return chave !== 'informacoes-de-pagamento';
    if (problemasPorPasso.has(chave)) return false;
    if (chave === 'informacoes-do-endosso')
      return (
        (endorsement.changes ?? []).length > 0 ||
        Boolean(endorsement.financial?.effective_date)
      );
    if (chave === 'motivo-e-documentos')
      return Boolean(endorsement.endorsement_reason_id);
    if (chave === 'resumo-da-alteracao') return endorsement.summary_confirmed ?? false;
    return false;
  }
  const steps = passosDoEndosso.map((chave) => ({
    key: chave,
    label: rotulosDosPassos[chave],
    state:
      rascunho && problemasPorPasso.has(chave)
        ? ('erro' as const)
        : concluido(chave)
          ? ('concluida' as const)
          : undefined,
  }));

  return (
    <div className="flex flex-col gap-5">
      <Trilha policy={policy} codigo={codigo} />
      <PageHeader
        title={tituloDoAssistente[codigo].titulo}
        description={`Endosso ${endorsement.number}`}
        meta={<StatusBadge kind="situacaoEndosso" value={endorsement.status} />}
        actions={
          emAndamento && podePedir ? (
            <Button variant="danger" onClick={() => setDesistindo(true)}>
              Cancelar cotacao
            </Button>
          ) : null
        }
      />

      <Card>
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <EndorsementInfo rotulo="No Apolice" valor={numeroDaApolice(policy)} />
          <EndorsementInfo
            rotulo="Tipo de endosso"
            valor={
              endorsement.endorsement_subtype?.name ?? tituloDoAssistente[codigo].titulo
            }
          />
          <EndorsementInfo
            rotulo="Tomador"
            valor={
              policy.parties?.policyholder?.legal_name ?? policy.policyholder.label
            }
          />
        </CardBody>
      </Card>

      <Stepper
        steps={steps}
        currentKey={passo.current}
        onStepClick={(chave) => passo.goTo(chave as PassoDoEndosso)}
        label="Passos do endosso"
      />

      {endorsement.status === 'cancelado' || endorsement.status === 'recusado' ? (
        <Alert
          tone="warning"
          title={
            endorsement.status === 'cancelado'
              ? 'Endosso cancelado'
              : 'Endosso recusado'
          }
        >
          {endorsement.status === 'cancelado'
            ? endorsement.withdrawal_note || 'Cancelado por quem pediu.'
            : endorsement.decision_note}
        </Alert>
      ) : null}

      {passo.current === 'informacoes-do-endosso' ? (
        codigo === 'neutro' ? (
          <NeutralEndorsementStep
            policy={policy}
            endorsement={endorsement}
            editavel={editavel}
            salvando={salvar.isPending}
            onProximo={(changes) => proximo(changes === null ? null : { changes })}
          />
        ) : (
          <FinancialEndorsementStep
            policy={policy}
            endorsement={endorsement}
            editavel={editavel}
            podePedir={podePedir}
            salvando={salvar.isPending}
            onProximo={proximo}
          />
        )
      ) : null}

      {passo.current === 'motivo-e-documentos' ? (
        <ReasonAndDocumentsStep
          endorsement={endorsement}
          editavel={editavel}
          podeEnviar={podePedir && emAndamento}
          salvando={salvar.isPending}
          onSalvar={(input) => salvar.mutate({ input, avancar: false })}
          onProximo={proximo}
        />
      ) : null}

      {passo.current === 'resumo-da-alteracao' ? (
        <ChangeSummaryStep
          policy={policy}
          endorsement={endorsement}
          editavel={editavel}
          confirmando={salvar.isPending}
          enviando={enviar.isPending}
          onConfirmar={(confirmado) =>
            salvar.mutate({ input: { summary_confirmed: confirmado }, avancar: false })
          }
          onVoltar={passo.back}
          onGerar={() => enviar.mutate()}
          onIrParaEmissao={passo.next}
        />
      ) : null}

      {passo.current === 'informacoes-de-pagamento' ? (
        <EndorsementPaymentStep
          endorsement={endorsement}
          policy={policy}
          onVoltar={passo.back}
        />
      ) : null}

      <Modal
        open={desistindo}
        onClose={() => setDesistindo(false)}
        title="Cancelar cotacao"
        description="Essa acao tem efeito permanente, deseja continuar?"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDesistindo(false)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={desistir.isPending}
              onClick={() => desistir.mutate()}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <Textarea
          label="Observacao da desistencia"
          value={nota}
          maxLength={2000}
          onChange={(event) => setNota(event.target.value)}
        />
      </Modal>
    </div>
  );
}
